interface ConfigComponent {
  id: string;
  quantity: number;
  width: number | null;
  height: number | null;
  thickness: number | null;
  source_model_name?: string | null;
  materials: { id: string; name: string } | null;
  model_components: {
    id: string;
    position: number;
    component_types: { id: string; name: string } | null;
  } | null;
}

interface ConfigComponentsSummaryProps {
  components: ConfigComponent[];
}

export function ConfigComponentsSummary({ components }: ConfigComponentsSummaryProps) {
  const activeComponents = components
    .filter(c => c.quantity > 0)
    .sort((a, b) => {
      const posA = a.model_components?.position ?? 999;
      const posB = b.model_components?.position ?? 999;
      return posA - posB;
    });
  
  if (activeComponents.length === 0) return null;
  
  const formatDimensions = (c: ConfigComponent) => {
    const parts: number[] = [];
    if (c.width) parts.push(Math.round(c.width * 1000));
    if (c.height) parts.push(Math.round(c.height * 1000));
    if (c.thickness) parts.push(Math.round(c.thickness * 1000));
    
    if (parts.length === 0) return null;
    return `${parts.join(' × ')} mm`;
  };

  // Ryhmittely source_model_name mukaan
  const groups = new Map<string, ConfigComponent[]>();
  for (const comp of activeComponents) {
    const key = comp.source_model_name || "";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(comp);
  }

  const renderComponent = (comp: ConfigComponent) => {
    const name = comp.model_components?.component_types?.name || "Tuntematon";
    const material = comp.materials?.name;
    const dims = formatDimensions(comp);
    const qty = comp.quantity > 1 ? ` × ${comp.quantity}` : "";
    
    return (
      <li key={comp.id} className="flex items-start gap-1">
        <span>•</span>
        <span>
          {name}{qty}
          {material && <span className="text-muted-foreground"> - {material}</span>}
          {dims && <span className="text-muted-foreground/70"> ({dims})</span>}
        </span>
      </li>
    );
  };
  
  return (
    <div className="mt-2 pl-4 border-l-2 border-amber-200 text-sm text-muted-foreground">
      <span className="text-xs font-medium text-amber-700">Sisältö:</span>
      <div className="mt-1 space-y-2">
        {Array.from(groups.entries()).map(([modelName, comps]) => (
          <div key={modelName || "__ungrouped"}>
            {modelName && (
              <div className="text-xs font-semibold text-blue-700 bg-blue-50 rounded px-1.5 py-0.5 inline-block mb-1">
                {modelName}
              </div>
            )}
            <ul className={`space-y-0.5 ${modelName ? "ml-3" : ""}`}>
              {comps.map(renderComponent)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
